import { subject } from '@casl/ability';
import { Card, Button, Tooltip } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialog } from 'src/hooks/use-dialog';

import { PageContainer } from '../../../../components/molecules/page-container';
import { PageHeader } from '../../../../components/molecules/page-header';
import { Plus } from '../../../../icons/plus';
import { useOrganizationAdmins } from '../../hooks/query/use-organization-admins';
import { useUserInfo } from '../../hooks/query/use-user-info';
import RemoveOrganizationAdminDialog from '../organisms/dialogs/remove-organization-admin-dialog';

import { ActionsTableMenu } from '@/components/atoms/actions-table-menu';
import InviteOrganizationAdminDialog from '@/features/organization-admins/components/organisms/dialogs/invite-organization-admin-dialog/invite-organization-admin-dialog';
import OrganizationAdminsTable, {
  OrganizationAdminsActionSlotPayload,
} from '@/features/organization-admins/components/organisms/organization-admins-table';
import { Can, usePermissions, withPermissions } from '@/permissions/can';
import { useUserStore } from '@/store/user-store';

const OrganizationAdmins = () => {
  const { t } = useTranslation(['organizationAdmins', 'common']);

  const permissions = usePermissions();

  const { data, isLoading, error } = useOrganizationAdmins();

  const { data: userInfoData } = useUserInfo();

  const userId = useUserStore(state => state.userId);

  const [isOpenInviteAdminDialog, handleOpenInviteAdminDialog, handleCloseInviteAdminDialog] =
    useDialog();

  const [selectedAdmin, setSelectedAdmin] = useState<OrganizationAdminsActionSlotPayload | null>(
    null,
  );

  const [isOpenRemoveAdminDialog, handleOpenRemoveAdminDialog, handleCloseRemoveAdminDialog] =
    useDialog();

  const actionsSlot = useCallback(({ id, email }: OrganizationAdminsActionSlotPayload) => {
    const isCurrentAdminSelfIdentityEqual = id?.toString() === userId;

    const actions = [
      {
        key: 'remove',
        label: t('organizationAdmins:action'),
        onClick: () => {
          setSelectedAdmin({ id, email });
          handleOpenRemoveAdminDialog();
        },
        disabled:
          permissions.cannot(
            'delete',
            subject('Administrators', {
              userId: id.toString(),
            }),
          ) || isCurrentAdminSelfIdentityEqual,
        tooltip: isCurrentAdminSelfIdentityEqual
          ? t('common:youCantRemoveYourself')
          : t('common:notSufficientPermissions'),
      },
    ];

    return <ActionsTableMenu actions={actions} />;
  }, []);

  return (
    <>
      <PageContainer helmetPageTitle={t('organizationAdmins:title')}>
        <PageHeader
          title={t('organizationAdmins:title')}
          extras={
            <Can I="create" a="Administrators" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      onClick={handleOpenInviteAdminDialog}
                      color="primary"
                      startIcon={<Plus fontSize="small" />}
                      variant="contained"
                      size="large"
                      disabled={!allowed}>
                      {t('organizationAdmins:inviteButton')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          }
        />
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}>
          <OrganizationAdminsTable
            data={data || []}
            isLoading={isLoading}
            error={error?.response?.data?.message || ''}
            actionsSlot={actionsSlot}
          />
        </Card>
      </PageContainer>
      <RemoveOrganizationAdminDialog
        open={isOpenRemoveAdminDialog}
        adminEmail={selectedAdmin?.email || ''}
        adminId={selectedAdmin?.id || 0}
        onCancel={handleCloseRemoveAdminDialog}
      />
      <InviteOrganizationAdminDialog
        open={isOpenInviteAdminDialog}
        onCancel={handleCloseInviteAdminDialog}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'Administrators'),
  OrganizationAdmins,
);
